import type { UiLanguage } from '@/types/generated/enums';

export const NAMESPACES = [
  'common',
  'nav',
  'auth',
  'settings',
  'enums',
  'dashboard',
  'reports',
  'copilot',
  'audiences',
  'onboarding',
  'assets',
  'billing',
  'goals',
  'insights',
  'strategy',
  'wizard',
  'support',
  'landing',
  'agency',
  'tour',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

// In the main chunk; every other namespace is fetched by the route loader that renders it.
export const EAGER_NAMESPACES = ['common', 'nav', 'enums'] as const;

export type EagerNamespace = (typeof EAGER_NAMESPACES)[number];

export type LazyNamespace = Exclude<Namespace, EagerNamespace>;

export type Bundle = Record<string, string>;

const PLACEHOLDER = /{{\s*-?\s*([\w.]+)[^}]*}}/g;

const placeholdersOf = (text: string): string =>
  [...text.matchAll(PLACEHOLDER)]
    .map((match) => match[1] ?? '')
    .toSorted()
    .join(',');

const PLURAL_KEY = /^(.+)_(zero|one|two|few|many|other)$/;

const ENGLISH_CATEGORIES = new Set<string>(
  new Intl.PluralRules('en').resolvedOptions().pluralCategories,
);

// A form English never selects (Spanish `many`) words the same counts English puts in `_other`.
export const englishCounterpart = (
  base: Bundle,
  key: string,
): string | undefined => {
  if (key in base) return base[key];
  const [, stem, category] = PLURAL_KEY.exec(key) ?? [];
  return stem && category && !ENGLISH_CATEGORIES.has(category)
    ? base[`${stem}_other`]
    : undefined;
};

const problemsOf = (
  key: string,
  text: string,
  translated: string | undefined,
): string[] => {
  // The extractor seeds a new key with '', which i18next resolves as a hit and renders blank.
  if (translated === undefined || translated === '') return [`missing: ${key}`];
  return placeholdersOf(text) === placeholdersOf(translated)
    ? []
    : [`placeholders: ${key}`];
};

// What `other` lacks, adds, or words with different placeholders, against the English `base`.
export const bundleProblems = (base: Bundle, other: Bundle): string[] => [
  ...Object.entries(base).flatMap(([key, text]) =>
    problemsOf(key, text, other[key]),
  ),
  ...Object.keys(other)
    .filter((key) => !(key in base))
    .flatMap((key) => {
      const text = englishCounterpart(base, key);
      return text === undefined
        ? [`extra: ${key}`]
        : problemsOf(key, text, other[key]);
    }),
];

// Plural forms a language's own rules can select, which English never needs (Spanish has `many`).
export const pluralProblems = (
  language: UiLanguage,
  bundle: Bundle,
): string[] => {
  const groups = new Map<string, Set<string>>();
  for (const key of Object.keys(bundle)) {
    const [, base, category] = PLURAL_KEY.exec(key) ?? [];
    if (base && category) {
      groups.set(base, (groups.get(base) ?? new Set()).add(category));
    }
  }
  const { pluralCategories } = new Intl.PluralRules(language).resolvedOptions();
  return [...groups]
    .filter(([, seen]) => seen.has('other'))
    .flatMap(([base, seen]) =>
      pluralCategories
        .filter((category) => !seen.has(category))
        .map((category) => `plurals: ${base}_${category}`),
    );
};

// A singular worded like its plural ("1 items"), whatever English does with the same family.
export const singularProblems = (bundle: Bundle): string[] =>
  Object.keys(bundle)
    .map((key) => /^(.+)_one$/.exec(key)?.[1])
    .filter((stem): stem is string => stem !== undefined)
    .filter((stem) => `${stem}_other` in bundle)
    .filter((stem) => bundle[`${stem}_one`] === bundle[`${stem}_other`])
    .map((stem) => `singular: ${stem}`);
